/**
 * TenantMembersList Component
 *
 * Displays a list of tenant members with:
 * - Member details (name, email, role)
 * - Role management (change roles)
 * - Member removal
 *
 * Migrated onto the List & Grid Standard's local primitives (`src/components/list/`) —
 * see `.specify/journals/list-grid-adoption-journal.md` and the reference migration
 * `src/pages/ApiKeysPage.tsx`. This is a CHILD component (rendered inside a tab panel by
 * `UsersRolesPage.tsx` / `IdentityPage.tsx`), so it uses `ListFrame` (embedded list), not
 * `ListPageLayout` (whole-page scaffold with `MasterDetailHeader`).
 */
import { useEffect, useMemo, useState } from 'react';
import type { FC, ReactNode } from 'react';
import {
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Avatar,
  Badge,
  Button,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Text,
  Title3,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Tooltip,
  tokens,
  makeStyles,
} from '@fluentui/react-components';
import {
  PeopleRegular,
  PersonDeleteRegular,
  ShieldPersonRegular,
  CheckmarkCircleRegular,
  ArrowSort16Regular,
  ArrowSortUp16Regular,
  ArrowSortDown16Regular,
} from '@fluentui/react-icons';
import { useTenantMembers, useUpdateUserRole, useRemoveTenantMember } from '../hooks';
import { getAvailableRoles } from '../services';
import type { TenantMember, TenantRole, UserStatus } from '../types';
import {
  ListFrame,
  ListToolbar,
  Pager,
  PAGE_SIZE,
  actionsColumnWidth,
  useListUrlState,
  makeSortComparator,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  ListLoadingOverlay,
} from '../../../components/list';
import type { SortDirection, SortState } from '../../../components/list';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  avatar: {
    marginRight: tokens.spacingHorizontalS,
  },
  nameCell: {
    display: 'flex',
    alignItems: 'center',
  },
  youBadge: {
    marginLeft: tokens.spacingHorizontalS,
  },
  statusActive: {
    color: tokens.colorPaletteGreenForeground1,
  },
  statusInactive: {
    color: tokens.colorNeutralForeground3,
  },
  statusSuspended: {
    color: tokens.colorPaletteRedForeground1,
  },
  actionsCell: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
  },
});

/**
 * Get badge color based on role
 */
function getRoleColor(role: TenantRole): 'brand' | 'important' | 'informative' | 'subtle' {
  switch (role) {
    case 'Owner':
      return 'important';
    case 'Admin':
      return 'brand';
    case 'SecurityEngineer':
    case 'Developer':
      return 'informative';
    default:
      return 'subtle';
  }
}

/**
 * Get status indicator
 */
function getStatusClass(status: UserStatus, styles: ReturnType<typeof useStyles>): string {
  switch (status) {
    case 'Active':
      return styles.statusActive;
    case 'Suspended':
      return styles.statusSuspended;
    default:
      return styles.statusInactive;
  }
}

/**
 * Get initials from display name
 */
function getInitials(displayName: string): string {
  const parts = displayName.split(' ').filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return displayName.substring(0, 2).toUpperCase();
}

/** Sort-field accessor for `makeSortComparator` — only the columns rendered `sortable` below. */
function getSortValue(item: TenantMember, field: string): unknown {
  switch (field) {
    case 'name':
      return item.displayName;
    case 'role':
      return item.role;
    case 'joinedAt':
      return item.joinedAt;
    default:
      return undefined;
  }
}

export interface TenantMembersListProps {
  tenantId: string;
  currentUserId?: string;
  canManageRoles?: boolean;
  canRemoveMembers?: boolean;
}

export const TenantMembersList: FC<TenantMembersListProps> = ({
  tenantId,
  currentUserId,
  canManageRoles = true,
  canRemoveMembers = true,
}) => {
  const styles = useStyles();
  const membersQuery = useTenantMembers(tenantId);
  const updateRole = useUpdateUserRole();
  const removeMember = useRemoveTenantMember();

  const [memberToRemove, setMemberToRemove] = useState<TenantMember | null>(null);
  const availableRoles = getAvailableRoles();

  const { state, setPage, setSort, setSearch } = useListUrlState({ defaultSort: 'name:asc' });

  const [sortField, sortDirRaw] = state.sort!.split(':');
  const sortDir: SortDirection = sortDirRaw === 'desc' ? 'desc' : 'asc';
  const sortState: SortState[] = [{ field: sortField, dir: sortDir }];

  const toggleSort = (field: string) => {
    if (field === sortField) {
      setSort(`${field}:${sortDir === 'asc' ? 'desc' : 'asc'}`);
    } else {
      setSort(`${field}:asc`);
    }
  };

  const sortIcon = (field: string) => {
    if (field !== sortField) return <ArrowSort16Regular />;
    return sortDir === 'asc' ? <ArrowSortUp16Regular /> : <ArrowSortDown16Regular />;
  };

  const rawMembers = useMemo(() => membersQuery.data ?? [], [membersQuery.data]);

  // Client-side search + sort + page slice — `useTenantMembers` already fetches the full
  // (unpaged) member set, so there's no server page to adapt via `useAdaptiveGridData`;
  // a plain in-memory slice over the array already in hand is the simpler, equivalent path.
  const filteredSorted = useMemo(() => {
    const needle = state.search.trim().toLowerCase();
    const rows = needle
      ? rawMembers.filter(
          (m) =>
            m.displayName.toLowerCase().includes(needle) || m.email.toLowerCase().includes(needle)
        )
      : rawMembers;
    return [...rows].sort(makeSortComparator(sortState, getSortValue));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawMembers, state.search, sortField, sortDir]);

  const totalCount = filteredSorted.length;
  const totalPages = Math.max(Math.ceil(totalCount / PAGE_SIZE), 1);
  const startIndex = (state.page - 1) * PAGE_SIZE;
  const pagedMembers = filteredSorted.slice(startIndex, startIndex + PAGE_SIZE);
  const hasPreviousPage = state.page > 1;
  const hasNextPage = state.page < totalPages;

  // Out-of-range clamp (§9) — never strand the user on an empty page after a remove/search.
  useEffect(() => {
    if (!membersQuery.isLoading && state.page > totalPages) {
      setPage(totalPages);
    }
  }, [membersQuery.isLoading, state.page, totalPages, setPage]);

  const handleRoleChange = async (member: TenantMember, newRole: TenantRole) => {
    await updateRole.mutateAsync({
      tenantId: member.tenantId,
      userId: member.userId,
      newRole,
    });
  };

  const handleRemoveMember = async () => {
    if (memberToRemove) {
      await removeMember.mutateAsync({
        tenantId: memberToRemove.tenantId,
        userId: memberToRemove.userId,
      });
      setMemberToRemove(null);
    }
  };

  const showActionsColumn = canManageRoles || canRemoveMembers;
  const actionButtonCount = (canManageRoles ? 1 : 0) + (canRemoveMembers ? 1 : 0);
  const columnCount = 5 + (showActionsColumn ? 1 : 0);

  let listBody: ReactNode;
  if (membersQuery.isLoading) {
    listBody = <LoadingSkeleton rows={6} columns={columnCount} />;
  } else if (membersQuery.isError) {
    listBody = (
      <ErrorState
        message={
          membersQuery.error instanceof Error ? membersQuery.error.message : undefined
        }
        onRetry={() => membersQuery.refetch()}
      />
    );
  } else if (rawMembers.length === 0) {
    listBody = (
      <EmptyState
        icon={<PeopleRegular />}
        title="No Team Members"
        description="Invite users to get started."
      />
    );
  } else if (pagedMembers.length === 0) {
    listBody = (
      <EmptyState
        icon={<PeopleRegular />}
        title="No Matching Members"
        description="Try adjusting your search."
      />
    );
  } else {
    listBody = (
      <ListLoadingOverlay busy={membersQuery.isFetching && !membersQuery.isLoading}>
        <Table aria-label="Team members">
          <TableHeader>
            <TableRow>
              <TableHeaderCell
                sortable
                sortDirection={sortField === 'name' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                sortIcon={sortIcon('name')}
                onClick={() => toggleSort('name')}
                style={{ width: '100%' }}
              >
                Name
              </TableHeaderCell>
              <TableHeaderCell>Email</TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={sortField === 'role' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                sortIcon={sortIcon('role')}
                onClick={() => toggleSort('role')}
              >
                Role
              </TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={
                  sortField === 'joinedAt' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
                }
                sortIcon={sortIcon('joinedAt')}
                onClick={() => toggleSort('joinedAt')}
              >
                Joined
              </TableHeaderCell>
              {showActionsColumn && (
                <TableHeaderCell style={{ width: actionsColumnWidth(actionButtonCount) }}>
                  Actions
                </TableHeaderCell>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {pagedMembers.map((member) => {
              const isCurrentUser = member.userId === currentUserId;
              const isOwner = member.role === 'Owner';
              const isRemoving =
                removeMember.isPending && removeMember.variables?.userId === member.userId;

              return (
                <TableRow key={member.userId}>
                  <TableCell>
                    <div className={styles.nameCell}>
                      <Avatar
                        name={member.displayName}
                        initials={getInitials(member.displayName)}
                        size={32}
                        color="colorful"
                        className={styles.avatar}
                      />
                      <Text weight="semibold">{member.displayName}</Text>
                      {isCurrentUser && (
                        <Badge appearance="outline" size="medium" className={styles.youBadge}>
                          You
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Text>{member.email}</Text>
                  </TableCell>
                  <TableCell>
                    <Badge appearance="filled" color={getRoleColor(member.role)}>
                      {member.role}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className={getStatusClass(member.status, styles)}>
                      {member.status === 'Active' && <CheckmarkCircleRegular />}
                      {' '}{member.status}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Text size={200}>{new Date(member.joinedAt).toLocaleDateString()}</Text>
                  </TableCell>
                  {showActionsColumn && (
                    <TableCell>
                      {/* Hidden-not-disabled (§18): the current user's own row and any Owner
                          row never expose role-change/remove actions — not even disabled. */}
                      {!isCurrentUser && !isOwner && (
                        <div className={styles.actionsCell}>
                          {canManageRoles && (
                            <Menu>
                              <MenuTrigger disableButtonEnhancement>
                                <Button
                                  appearance="subtle"
                                  icon={<ShieldPersonRegular />}
                                  size="small"
                                  aria-label="Change Role"
                                />
                              </MenuTrigger>
                              <MenuPopover>
                                <MenuList>
                                  {availableRoles
                                    .filter((role) => role !== 'Owner')
                                    .map((role) => (
                                      <MenuItem key={role} onClick={() => handleRoleChange(member, role)}>
                                        {role}
                                        {member.role === role && ' (current)'}
                                      </MenuItem>
                                    ))}
                                </MenuList>
                              </MenuPopover>
                            </Menu>
                          )}
                          {canRemoveMembers && (
                            <Tooltip content="Remove Member" relationship="label">
                              <Button
                                appearance="subtle"
                                icon={<PersonDeleteRegular />}
                                size="small"
                                aria-label="Remove Member"
                                disabled={isRemoving}
                                onClick={() => setMemberToRemove(member)}
                              />
                            </Tooltip>
                          )}
                        </div>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </ListLoadingOverlay>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.headerRow}>
        <Title3>Team Members</Title3>
      </div>

      <ListToolbar
        search={state.search}
        onSearchChange={setSearch}
        searchPlaceholder="Search members…"
        onRefresh={() => membersQuery.refetch()}
        isRefreshing={membersQuery.isFetching}
        testIdPrefix="tenant-members"
      />

      <ListFrame>{listBody}</ListFrame>

      <Pager
        page={state.page}
        totalPages={totalPages}
        hasPreviousPage={hasPreviousPage}
        hasNextPage={hasNextPage}
        totalCount={totalCount}
        onPageChange={setPage}
        testId="tenant-members-pager"
      />

      {/* Remove Member Confirmation Dialog */}
      <Dialog
        open={!!memberToRemove}
        onOpenChange={(_, data) => !data.open && setMemberToRemove(null)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Remove Team Member</DialogTitle>
            <DialogContent>
              <Text>
                Are you sure you want to remove{' '}
                <Text weight="semibold">{memberToRemove?.displayName}</Text> from this
                tenant? They will lose access to all resources.
              </Text>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setMemberToRemove(null)}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleRemoveMember}
                disabled={removeMember.isPending}
              >
                {removeMember.isPending ? 'Removing...' : 'Remove Member'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};
